import * as React from "react"
import { Autocomplete, FilterOptionsState, TextField } from "@mui/material"
import { Option } from "@web/components/Forms/types"
import { VariableOptions } from "platform-common/util"
import { useBeautifulMentions } from "lexical-beautiful-mentions"

import styles from "./VariablesPlugin.module.scss"
import { TextEditorControllerContext } from "../../TextEditor.context"

const EMPTY_OPTION = { value: "", label: "" }

interface Props {
  /**
   * Will be called instead of internal variable insertion logic.
   */
  onVariableSelected?: (value: string) => void
  onVariableMenuOpen?: () => void
  onVariableMenuClose?: () => void
}

export const VariableSelector = ({ onVariableSelected, onVariableMenuOpen, onVariableMenuClose }: Props) => {
  const { insertMention } = useBeautifulMentions()

  const filterOptions = (options: Option[], { inputValue }: FilterOptionsState<Option>) => {
    const normalized = inputValue?.trim().toLowerCase() || ""
    const filtered = options.filter((i) => i.value && i.value.includes(normalized))

    return filtered.length ? filtered : [{ value: `${normalized}}}`, label: normalized }]
  }

  const handleVariableSelected = React.useCallback(
    (_: unknown, { value }: Option) => {
      if (onVariableSelected) {
        onVariableSelected(value)
        return
      }

      if (value === "other}}") {
        insertMention({ trigger: "{{", value: "" })
        return
      }

      insertMention({ trigger: "{{", value })
    },
    [insertMention, onVariableSelected]
  )

  const { setControls } = React.useContext(TextEditorControllerContext)

  React.useEffect(() => {
    setControls({ insertVariable: (value) => handleVariableSelected(null, { value, label: value }) })
  }, [handleVariableSelected, setControls])

  return (
    <Autocomplete<Option<string>, false, true>
      options={[...VariableOptions, EMPTY_OPTION]}
      value={EMPTY_OPTION}
      onChange={handleVariableSelected}
      filterSelectedOptions
      filterOptions={filterOptions}
      renderInput={(p) => <TextField {...p} placeholder="Insert Variable" />}
      disableClearable
      disablePortal
      className={styles.variableSelector}
      onOpen={onVariableMenuOpen}
      onClose={onVariableMenuClose}
    />
  )
}
